import React, { useEffect, useState } from "react";
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, MenuItem, Select, TextField, Typography
} from "@mui/material";
import { Add as AddIcon, DeleteOutline as DeleteIcon } from "@mui/icons-material";

// How a column's value is written, kept on its mapping as "Transforms": steps the output file generator
// applies in order. This screen edits one step per column -- a date format, a value map, a pattern or a
// list item.
export const DATE_FORMATS = [
  { value: "M/d/yyyy", example: "6/16/2026" },
  { value: "MM/dd/yyyy", example: "06/16/2026" },
  { value: "yyyy-MM-dd", example: "2026-06-16" },
];

// The generator's rule for a pattern: digit slots (#) and the separators between them, at least one slot.
export const PATTERN_ALLOWED = /^(?=.*#)[#\-() ./+]{1,40}$/;

/** The pattern filled with 1, 2, 3 ... -- what a number written through it looks like. */
export function patternExample(pattern) {
  let next = 0;
  return [...pattern].map(c => (c === "#" ? String((next++ % 9) + 1) : c)).join("");
}

// The generator's rule for a list separator: one to three punctuation characters, never a letter or digit.
export const SEPARATOR_ALLOWED = /^[^\p{L}\p{N}\s]{1,3}$/u;
export const SPLIT_MAX_POSITION = 50;

/**
 * Item `position` (from 1) of a list held as text, as the generator picks it: split on `separator`, each item
 * trimmed, empty items and those in `leaveOut` (ignoring case) dropped. Null when there is no such item.
 */
export function splitItem(value, separator, position, leaveOut) {
  const skip = new Set((leaveOut || []).map(v => v.trim().toLowerCase()).filter(Boolean));
  const items = String(value ?? "").split(separator).map(v => v.trim()).filter(v => v && !skip.has(v.toLowerCase()));
  return items[position - 1] ?? null;
}

const leaveOutList = text => String(text || "").split("\n").map(v => v.trim()).filter(Boolean);

const read = (step, name) => step?.[name] ?? step?.[name.charAt(0).toLowerCase() + name.slice(1)];

/** The step this screen can edit, or null when the column has none (or only steps it does not know). */
export function editableStep(transforms, replacements) {
  const steps = Array.isArray(transforms) ? transforms : [];
  if (steps.length === 1) {
    const type = String(read(steps[0], "Type") || "").toLowerCase();
    if (type === "date" || type === "map" || type === "pattern" || type === "split") return steps[0];
  }
  if (steps.length === 0 && replacements && Object.keys(replacements).length > 0) {
    return { Type: "map", Map: { ...replacements }, Otherwise: "keep" };
  }
  return null;
}

/** One line saying what a column's transforms do, for the icon's tooltip. */
export function describeTransforms(transforms, replacements) {
  const steps = Array.isArray(transforms) ? transforms : [];
  if (steps.length === 0) {
    const count = replacements ? Object.keys(replacements).length : 0;
    return count > 0 ? `Value map (${count})` : "";
  }
  return steps.map(step => {
    const type = String(read(step, "Type") || "").toLowerCase();
    if (type === "date") return `Date as ${read(step, "Format") || "M/d/yyyy"}${read(step, "AsDate") === false ? " (text)" : ""}`;
    if (type === "map") return `Value map (${Object.keys(read(step, "Map") || {}).length})`;
    if (type === "pattern") return `Pattern ${read(step, "Pattern") || ""}`;
    if (type === "split") return `List item ${read(step, "Position") || ""} (split on ${read(step, "Separator") || ";"})`;
    return `${read(step, "Type") || "Unknown"} step`;
  }).join(", ");
}

export default function ColumnTransformDialog({ open, columnLabel, transforms, replacements, onSave, onRemove, onClose }) {
  const step = editableStep(transforms, replacements);
  const hasOther = !step && ((Array.isArray(transforms) && transforms.length > 0));
  const [type, setType] = useState("date");
  const [format, setFormat] = useState(DATE_FORMATS[0].value);
  const [asDate, setAsDate] = useState(true);
  const [rows, setRows] = useState([{ from: "", to: "" }]);
  const [otherwise, setOtherwise] = useState("keep");
  const [otherwiseText, setOtherwiseText] = useState("");
  const [pattern, setPattern] = useState("###-##-####");
  const [separator, setSeparator] = useState(";");
  const [position, setPosition] = useState("1");
  const [leaveOut, setLeaveOut] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const stepType = String(read(step, "Type") || "date").toLowerCase();
    setType(["map", "pattern", "split"].includes(stepType) ? stepType : "date");
    setPattern(read(step, "Pattern") || "###-##-####");
    setSeparator(read(step, "Separator") || ";");
    setPosition(String(read(step, "Position") || "1"));
    setLeaveOut((read(step, "LeaveOut") || []).join("\n"));
    setFormat(DATE_FORMATS.some(f => f.value === read(step, "Format")) ? read(step, "Format") : DATE_FORMATS[0].value);
    setAsDate(read(step, "AsDate") !== false);
    const map = read(step, "Map") || {};
    const mapRows = Object.entries(map).map(([from, to]) => ({ from, to: to ?? "" }));
    setRows(mapRows.length > 0 ? mapRows : [{ from: "", to: "" }]);
    // A list with no such item is blanked unless the step says otherwise; a value a map does not name is kept.
    const fallback = stepType === "split" ? "blank" : "keep";
    const other = String(read(step, "Otherwise") || fallback).toLowerCase();
    setOtherwise(["keep", "blank", "value"].includes(other) ? other : fallback);
    setOtherwiseText(read(step, "OtherwiseText") || "");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSave = () => {
    if (type === "date") {
      onSave([{ Type: "date", Format: format, AsDate: asDate }]);
      return;
    }
    if (type === "pattern") {
      const trimmed = pattern.trim();
      if (!PATTERN_ALLOWED.test(trimmed)) {
        setError("Use # for each digit and only - ( ) . / + or spaces between them, e.g. ###-##-####.");
        return;
      }
      onSave([{ Type: "pattern", Pattern: trimmed }]);
      return;
    }
    if (type === "split") {
      const sep = separator.trim();
      const pos = Number(position);
      if (!SEPARATOR_ALLOWED.test(sep)) {
        setError("The separator is one to three punctuation characters, e.g. ; or , or |.");
        return;
      }
      if (!Number.isInteger(pos) || pos < 1 || pos > SPLIT_MAX_POSITION) {
        setError(`The item number is a whole number from 1 to ${SPLIT_MAX_POSITION}.`);
        return;
      }
      if (otherwise === "value" && !otherwiseText.trim()) {
        setError("Enter the text to write when there is no such item.");
        return;
      }
      const skip = leaveOutList(leaveOut);
      onSave([{
        Type: "split",
        Separator: sep,
        Position: pos,
        ...(skip.length > 0 ? { LeaveOut: skip } : {}),
        Otherwise: otherwise,
        ...(otherwise === "value" ? { OtherwiseText: otherwiseText } : {})
      }]);
      return;
    }
    const map = {};
    for (const row of rows) {
      const from = (row.from ?? "").trim();
      const to = row.to ?? "";
      if (from === "" && to === "") continue;
      if (Object.keys(map).some(k => k.toLowerCase() === from.toLowerCase())) {
        setError(`"${from || "(empty)"}" is listed twice.`);
        return;
      }
      map[from] = to;
    }
    if (Object.keys(map).length === 0) {
      setError("Add at least one value to map.");
      return;
    }
    if (otherwise === "value" && !otherwiseText.trim()) {
      setError("Enter the text for values not listed.");
      return;
    }
    onSave([{
      Type: "map",
      Map: map,
      Otherwise: otherwise,
      ...(otherwise === "value" ? { OtherwiseText: otherwiseText } : {})
    }]);
  };

  const labelSx = { fontSize: "12px", color: "#344049", fontWeight: 600, mb: 0.5 };
  const inputSx = { "& .MuiInputBase-input": { fontSize: "12px" } };
  const example = DATE_FORMATS.find(f => f.value === format)?.example;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, color: "#344049", pb: 1 }}>
        Data transformation – {columnLabel}
      </DialogTitle>
      <DialogContent dividers>
        {hasOther ? (
          <Typography sx={{ fontSize: "12px", color: "#6b7684" }}>
            This column has transforms this screen can't edit ({describeTransforms(transforms, replacements)}).
            They are kept as they are; use Remove to clear them.
          </Typography>
        ) : (
          <>
            <Typography sx={labelSx}>Transformation</Typography>
            <Select size="small" fullWidth value={type} onChange={e => {
              const next = e.target.value;
              // Each kind's own default for what it cannot place: a list blanks, a map keeps.
              if (next !== type && (next === "split" || type === "split")) setOtherwise(next === "split" ? "blank" : "keep");
              setType(next);
              setError("");
            }} sx={{ fontSize: "12px", mb: 2 }}>
              <MenuItem value="date" sx={{ fontSize: "12px" }}>Date format</MenuItem>
              <MenuItem value="map" sx={{ fontSize: "12px" }}>Value mapping</MenuItem>
              <MenuItem value="pattern" sx={{ fontSize: "12px" }}>Pattern</MenuItem>
              <MenuItem value="split" sx={{ fontSize: "12px" }}>Item from a list</MenuItem>
            </Select>

            {type === "split" ? (
              <>
                <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Separator</Typography>
                    <TextField size="small" fullWidth sx={inputSx} placeholder=";" value={separator}
                      onChange={e => { setSeparator(e.target.value); setError(""); }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={labelSx}>Item number</Typography>
                    <TextField size="small" fullWidth sx={inputSx} type="number" inputProps={{ min: 1, max: SPLIT_MAX_POSITION }} value={position}
                      onChange={e => { setPosition(e.target.value); setError(""); }} />
                  </Box>
                </Box>
                <Typography sx={labelSx}>Leave out (one per line)</Typography>
                <TextField size="small" fullWidth multiline minRows={2} sx={{ mb: 2, ...inputSx }} placeholder="e.g. English" value={leaveOut}
                  onChange={e => setLeaveOut(e.target.value)} />

                <Typography sx={labelSx}>When there is no such item</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Select size="small" value={otherwise} onChange={e => setOtherwise(e.target.value)} sx={{ fontSize: "12px", minWidth: 200 }}>
                    <MenuItem value="blank" sx={{ fontSize: "12px" }}>Leave blank</MenuItem>
                    <MenuItem value="value" sx={{ fontSize: "12px" }}>Write a fixed text</MenuItem>
                    <MenuItem value="keep" sx={{ fontSize: "12px" }}>Keep the whole value</MenuItem>
                  </Select>
                  {otherwise === "value" && (
                    <TextField size="small" sx={{ flex: 1, ...inputSx }} placeholder="Text" value={otherwiseText} onChange={e => setOtherwiseText(e.target.value)} />
                  )}
                </Box>
                <Typography sx={{ fontSize: "11px", color: "#6b7684", mt: 1 }}>
                  The value is split on the separator; items are trimmed, and empty ones and those left out (ignoring case) are skipped.
                  {SEPARATOR_ALLOWED.test(separator.trim()) && Number(position) >= 1 && (() => {
                    const sep = separator.trim();
                    const sample = ["Hindi", "English", "Urdu"].join(sep);
                    const item = splitItem(sample, sep, Number(position), leaveOutList(leaveOut));
                    const none = otherwise === "value" ? `"${otherwiseText}"` : otherwise === "keep" ? "the whole value" : "blank";
                    return <> For example, item {Number(position)} of "{sample}" is {item ? `"${item}"` : none}.</>;
                  })()}
                </Typography>
              </>
            ) : type === "pattern" ? (
              <>
                <Typography sx={labelSx}>Pattern</Typography>
                <TextField size="small" fullWidth sx={{ mb: 1, ...inputSx }} placeholder="###-##-####" value={pattern}
                  onChange={e => { setPattern(e.target.value); setError(""); }} />
                <Typography sx={{ fontSize: "11px", color: "#6b7684", mt: 1 }}>
                  Each # takes the next digit; other characters are written as they stand.
                  {PATTERN_ALLOWED.test(pattern.trim()) && <> A number such as {patternExample(pattern.trim()).replace(/[^0-9]/g, "")} is written as {patternExample(pattern.trim())}.</>}
                  {" "}A value with letters in it, or with more or fewer digits than the pattern has #, is written as it is.
                </Typography>
              </>
            ) : type === "date" ? (
              <>
                <Typography sx={labelSx}>Format</Typography>
                <Select size="small" fullWidth value={format} onChange={e => setFormat(e.target.value)} sx={{ fontSize: "12px", mb: 1 }}>
                  {DATE_FORMATS.map(f => (
                    <MenuItem key={f.value} value={f.value} sx={{ fontSize: "12px" }}>{f.value} (e.g. {f.example})</MenuItem>
                  ))}
                </Select>
                <FormControlLabel
                  control={<Checkbox size="small" checked={asDate} onChange={e => setAsDate(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: "12px" }}>Write as an Excel date (otherwise as text)</Typography>}
                />
                <Typography sx={{ fontSize: "11px", color: "#6b7684", mt: 1 }}>
                  A date held as text (2026-06-16, 6/16/2026) is written as {example}{asDate ? ", as a real date Excel can sort and filter" : ", as text"}.
                  A value that is not a date is written as it is; the 1900 "no date" is left blank.
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
                  <Typography sx={{ ...labelSx, flex: 1, mb: 0 }}>Value</Typography>
                  <Typography sx={{ ...labelSx, flex: 1, mb: 0 }}>Write as</Typography>
                  <Box sx={{ width: 32 }} />
                </Box>
                {rows.map((row, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                    <TextField size="small" sx={{ flex: 1, ...inputSx }} placeholder="e.g. M (empty = no value)" value={row.from}
                      onChange={e => setRows(prev => prev.map((r, j) => j === i ? { ...r, from: e.target.value } : r))} />
                    <TextField size="small" sx={{ flex: 1, ...inputSx }} placeholder="e.g. Male (empty = blank)" value={row.to}
                      onChange={e => setRows(prev => prev.map((r, j) => j === i ? { ...r, to: e.target.value } : r))} />
                    <IconButton size="small" aria-label="remove-row" onClick={() => setRows(prev => prev.length > 1 ? prev.filter((_, j) => j !== i) : [{ from: "", to: "" }])}>
                      <DeleteIcon sx={{ fontSize: 18, color: "#969696" }} />
                    </IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<AddIcon />} sx={{ color: "#FF8200", fontSize: "12px", mb: 2 }}
                  onClick={() => setRows(prev => [...prev, { from: "", to: "" }])}>
                  Add value
                </Button>

                <Typography sx={labelSx}>Values not listed</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Select size="small" value={otherwise} onChange={e => setOtherwise(e.target.value)} sx={{ fontSize: "12px", minWidth: 200 }}>
                    <MenuItem value="keep" sx={{ fontSize: "12px" }}>Keep as they are</MenuItem>
                    <MenuItem value="blank" sx={{ fontSize: "12px" }}>Leave blank</MenuItem>
                    <MenuItem value="value" sx={{ fontSize: "12px" }}>Write a fixed text</MenuItem>
                  </Select>
                  {otherwise === "value" && (
                    <TextField size="small" sx={{ flex: 1, ...inputSx }} placeholder="Text" value={otherwiseText} onChange={e => setOtherwiseText(e.target.value)} />
                  )}
                </Box>
                <Typography sx={{ fontSize: "11px", color: "#6b7684", mt: 1 }}>
                  Values are matched ignoring case and spaces. A Y/N field can be mapped as Y and N or as True and False.
                </Typography>
              </>
            )}
            <Typography sx={{ fontSize: "11px", color: "#6b7684", mt: 1 }}>
              A column with a transformation is written as a value; a formula in the template for it is not used.
            </Typography>
          </>
        )}
        {error && <Typography sx={{ fontSize: "12px", color: "#d32f2f", mt: 1 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        {(step || hasOther) && (
          <Button sx={{ mr: "auto", color: "#d32f2f", fontSize: "12px" }} onClick={onRemove}>Remove</Button>
        )}
        <Button sx={{ color: "#6b7684", fontSize: "12px" }} onClick={onClose}>Cancel</Button>
        {!hasOther && (
          <Button variant="contained" sx={{ backgroundColor: "#FF8200", fontSize: "12px", "&:hover": { backgroundColor: "#e67500" } }} onClick={handleSave}>
            Apply
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
